import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spinner, Modal, Button, Form } from 'react-bootstrap';
import { getLessonById, submitAnswers, addMcqToLesson } from '../../api/lessons';
import './LessonDetail.scss';

export const LessonDetail = () => {
  const location = useLocation();
  const id = location.state?.id;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [showAddMcqModal, setShowAddMcqModal] = useState(false);
  const [newMcq, setNewMcq] = useState({ question: '', quizType: 'singleChoice', options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }], paragraphRightAnswer: '' });
  const [addingMcq, setAddingMcq] = useState(false);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        setLoading(true);
        const res = await getLessonById(id);
        setData(res.data || res);
      } catch (err) {
        console.error(err);
        setError('Failed to load course details.');
      } finally {
        setLoading(false);
      }
    };
    fetchLesson();
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-white">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error || !data || !data._id) {
    return (
      <div className="text-center py-5 bg-white vh-100">
        <h4 className="text-danger">{error || 'Course not found'}</h4>
        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const { lesson = data, pdfs = [], mcqs = [], videos = [] } = data;
  const isCompleted = data?.isAnswered || data?.isCompleted || false;

  const handleSubmitAll = async () => {
    try {
      const answersArray = Object.keys(userAnswers).map(mcqId => userAnswers[mcqId]);
      if (answersArray.length === 0 && mcqs.length > 0) {
         alert("Please answer at least one question.");
         return;
      }
      await submitAnswers({ lessonId: id, answers: answersArray });
      alert("Answers submitted successfully!");
      // Reload data
      setLoading(true);
      const res = await getLessonById(id);
      setData(res.data || res);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit answers.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMcq = async (e) => {
    e.preventDefault();
    if (!newMcq.question.trim()) return;
    try {
      setAddingMcq(true);
      await addMcqToLesson(id, newMcq);
      alert('MCQ added successfully!');
      setShowAddMcqModal(false);
      setNewMcq({ question: '', quizType: 'singleChoice', options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }], paragraphRightAnswer: '' });
      // Reload data
      setLoading(true);
      const res = await getLessonById(id);
      setData(res.data || res);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add MCQ.');
    } finally {
      setAddingMcq(false);
      setLoading(false);
    }
  };

  return (
    <div className="cd-course-details-page bg-white min-vh-100 d-flex flex-column pb-5">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between p-3 position-sticky top-0 bg-white" style={{ zIndex: 10 }}>
        <button className="btn btn-link text-dark p-0" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left fs-4"></i>
        </button>
        <h5 className="mb-0 fw-bold">Course Details</h5>
        <button className="btn btn-outline-primary btn-sm" onClick={() => setShowAddMcqModal(true)}>
          <i className="bi bi-plus-lg me-1"></i> Add MCQ
        </button>
      </div>

      <div className="p-3 d-flex flex-column gap-3 mx-auto" style={{ maxWidth: '600px', width: '100%' }}>
        
        {/* Status */}
        {isCompleted && (
          <div className="text-center mb-2 mt-2">
            <div className="d-inline-flex align-items-center justify-content-center bg-success text-white rounded-circle mb-3" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-check-lg" style={{ fontSize: '2.5rem' }}></i>
            </div>
            <h5 className="fw-semibold text-dark">You have completed the lesson.</h5>
          </div>
        )}

        {/* Title */}
        <div className="cd-detail-card p-3 rounded-4 bg-light">
          <p className="text-dark mb-1 fs-6">Course title:</p>
          <p className="fw-normal mb-0 text-muted">{lesson.title}</p>
        </div>

        {/* Description */}
        <div className="cd-detail-card p-3 rounded-4 bg-light">
          <p className="text-dark mb-1 fs-6">Course description:</p>
          <p className="fw-normal mb-0 text-muted" style={{ whiteSpace: 'pre-line' }}>
            {lesson.description || 'No description available.'}
          </p>
        </div>

        {/* Video Link */}
        {lesson.media && (
          <div className="cd-detail-card p-3 rounded-4 bg-light">
            <p className="text-muted mb-2 fs-6">Video Link:</p>
            <div className="ratio ratio-16x9 rounded-3 overflow-hidden bg-dark">
              {lesson.media.includes('youtube.com') || lesson.media.includes('vimeo.com') ? (
                <iframe src={lesson.media.replace('watch?v=', 'embed/')} allowFullScreen title="Video"></iframe>
              ) : (
                <a href={lesson.media} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center justify-content-center h-100 text-white text-decoration-none">
                  <i className="bi bi-play-circle-fill fs-1 me-2"></i> View Video Link
                </a>
              )}
            </div>
          </div>
        )}

        {/* Uploaded Videos */}
        {videos.map((vid) => (
          <div key={vid._id} className="cd-detail-card p-3 rounded-4 bg-light">
            <p className="text-muted mb-2 fs-6">Uploaded Video:</p>
            <div className="ratio ratio-16x9 rounded-3 overflow-hidden bg-dark">
              <video controls src={vid.url} className="w-100 h-100 object-fit-cover"></video>
            </div>
          </div>
        ))}

        {/* PDFs */}
        {pdfs.map((pdf) => (
          <div key={pdf._id} className="cd-detail-card p-3 rounded-4 bg-light d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3 overflow-hidden pe-2">
              <i className="bi bi-file-earmark-pdf-fill text-danger fs-3"></i>
              <span className="text-dark fs-6 text-truncate">PDF - {pdf.title || pdf.url.split('/').pop()}</span>
            </div>
            {pdf.url && (
              <a href={pdf.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                <i className="bi bi-eye-fill text-dark fs-4"></i>
              </a>
            )}
          </div>
        ))}

        {/* Questions */}
        {mcqs.map((mcq, index) => (
          <div key={mcq._id} className="cd-detail-card p-4 rounded-4 bg-light">
            <h6 className="fw-bold mb-3">Q{index + 1}: {mcq.question}</h6>
            
            {/* Paragraph / Text Question */}
            {mcq.quizType === 'paragraph' && (
              <div className="cd-text-answer p-3 rounded-3 mt-2">
                {!mcq.userAnswer ? (
                  <>
                    <p className="fw-bold mb-1 small text-dark">Your Answer:</p>
                    <textarea 
                      className="form-control mb-3" 
                      rows={3} 
                      placeholder="Write your answer here..."
                      value={userAnswers[mcq._id]?.paragraphAnswer || ''}
                      onChange={(e) => setUserAnswers({
                        ...userAnswers,
                        [mcq._id]: { mcqId: mcq._id, quizType: 'paragraph', paragraphAnswer: e.target.value }
                      })}
                    ></textarea>
                  </>
                ) : (
                  <>
                    <p className="fw-bold mb-1 small text-dark">Your Answer:</p>
                    <p className="mb-3 text-dark">
                      {typeof mcq.userAnswer === 'object' && mcq.userAnswer !== null 
                        ? (mcq.userAnswer.paragraphAnswer || 'No answer provided') 
                        : (mcq.userAnswer || 'No answer provided')}
                    </p>
                    <p className="fw-bold mb-1 small text-dark">Correct Answer:</p>
                    <p className="mb-0 text-muted">{mcq.paragraphRightAnswer || 'No correct answer provided'}</p>
                  </>
                )}
              </div>
            )}

            {/* Multiple/Single Choice */}
            {(mcq.quizType === 'singleChoice' || mcq.quizType === 'multipleChoice') && (
              <div className="d-flex flex-column gap-3 mt-3">
                {mcq.options.map((opt) => {
                  let wrapperClass = "cd-option-wrapper p-3 rounded-3 d-flex align-items-center gap-3";
                  let iconClass = mcq.quizType === 'singleChoice' ? "bi-circle" : "bi-square";
                  
                  const ansState = userAnswers[mcq._id] || {};
                  const isSelected = mcq.quizType === 'singleChoice' 
                    ? ansState.selectedOptionId === opt._id
                    : (ansState.selectedOptionIds || []).includes(opt._id);

                  if (mcq.userAnswer) {
                    // Show correct/incorrect styling if answered
                    if (opt.isCorrect) {
                      wrapperClass += " correct-ans bg-success bg-opacity-10 border-success border";
                      iconClass = mcq.quizType === 'singleChoice' ? "bi-check-circle-fill text-success" : "bi-check-square-fill text-success";
                    } else {
                      wrapperClass += " default-ans bg-white border";
                      iconClass = mcq.quizType === 'singleChoice' ? "bi-circle" : "bi-square";
                    }
                  } else {
                    // Interactive mode if not answered
                    wrapperClass += " default-ans bg-white border pointer";
                    if (isSelected) {
                      wrapperClass += " bg-primary bg-opacity-10 border-primary";
                      iconClass = mcq.quizType === 'singleChoice' ? "bi-check-circle-fill text-primary" : "bi-check-square-fill text-primary";
                    } else {
                      iconClass = mcq.quizType === 'singleChoice' ? "bi-circle" : "bi-square";
                    }
                  }

                  const handleOptionClick = () => {
                    if (mcq.userAnswer) return;
                    if (mcq.quizType === 'singleChoice') {
                       setUserAnswers({
                         ...userAnswers,
                         [mcq._id]: { mcqId: mcq._id, quizType: 'singleChoice', selectedOptionId: opt._id }
                       });
                    } else {
                       const currentSelected = ansState.selectedOptionIds || [];
                       const newSelected = currentSelected.includes(opt._id) 
                         ? currentSelected.filter(id => id !== opt._id) 
                         : [...currentSelected, opt._id];
                       setUserAnswers({
                         ...userAnswers,
                         [mcq._id]: { mcqId: mcq._id, quizType: 'multipleChoice', selectedOptionIds: newSelected }
                       });
                    }
                  };

                  return (
                    <div key={opt._id} className={wrapperClass} style={{ cursor: mcq.userAnswer ? 'default' : 'pointer' }} onClick={handleOptionClick}>
                      <i className={`bi ${iconClass} fs-5`}></i>
                      <span className="fw-normal">{opt.text}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* Submit All Answers Button */}
        {mcqs.length > 0 && !mcqs.every(m => m.userAnswer) && (
          <div className="text-center mt-4">
            <button className="btn btn-primary rounded-pill px-5 py-2 fs-5" onClick={handleSubmitAll}>
              Submit All Answers
            </button>
          </div>
        )}

      </div>

      {/* Add MCQ Modal */}
      <Modal show={showAddMcqModal} onHide={() => setShowAddMcqModal(false)} centered>
        <Form onSubmit={handleAddMcq}>
          <Modal.Header closeButton>
            <Modal.Title>Add New MCQ</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Question</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                required
                placeholder="Enter the question text"
                value={newMcq.question}
                onChange={(e) => setNewMcq({ ...newMcq, question: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Quiz Type</Form.Label>
              <Form.Select 
                value={newMcq.quizType}
                onChange={(e) => setNewMcq({ ...newMcq, quizType: e.target.value })}
              >
                <option value="singleChoice">Single Choice</option>
                <option value="multipleChoice">Multiple Choice</option>
                <option value="paragraph">Paragraph</option>
              </Form.Select>
            </Form.Group>

            {newMcq.quizType === 'paragraph' ? (
              <Form.Group className="mb-3">
                <Form.Label>Correct Answer</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={2} 
                  placeholder="Enter the expected correct answer"
                  value={newMcq.paragraphRightAnswer}
                  onChange={(e) => setNewMcq({ ...newMcq, paragraphRightAnswer: e.target.value })}
                />
              </Form.Group>
            ) : (
              <div className="mb-3">
                <Form.Label>Options</Form.Label>
                {newMcq.options.map((opt, idx) => (
                  <div key={idx} className="d-flex align-items-center mb-2 gap-2">
                    <Form.Check 
                      type={newMcq.quizType === 'singleChoice' ? 'radio' : 'checkbox'}
                      name={newMcq.quizType === 'singleChoice' ? 'mcq_option_radio' : `mcq_option_${idx}`}
                      checked={opt.isCorrect}
                      onChange={(e) => {
                        const newOptions = [...newMcq.options];
                        if (newMcq.quizType === 'singleChoice') {
                          newOptions.forEach(o => o.isCorrect = false);
                        }
                        newOptions[idx].isCorrect = e.target.checked;
                        setNewMcq({ ...newMcq, options: newOptions });
                      }}
                    />
                    <Form.Control 
                      type="text" 
                      placeholder={`Option ${idx + 1}`}
                      required
                      value={opt.text}
                      onChange={(e) => {
                        const newOptions = [...newMcq.options];
                        newOptions[idx].text = e.target.value;
                        setNewMcq({ ...newMcq, options: newOptions });
                      }}
                    />
                    {newMcq.options.length > 2 && (
                      <Button variant="outline-danger" size="sm" onClick={() => {
                        const newOptions = newMcq.options.filter((_, i) => i !== idx);
                        setNewMcq({ ...newMcq, options: newOptions });
                      }}>
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                  setNewMcq({ ...newMcq, options: [...newMcq.options, { text: '', isCorrect: false }] });
                }}>
                  <i className="bi bi-plus"></i> Add Option
                </Button>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddMcqModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={addingMcq || !newMcq.question.trim()}>
              {addingMcq ? <Spinner animation="border" size="sm" /> : 'Add Question'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </div>
  );
};
